/**
 * Static content pages (Contact, FAQ, Shipping & Returns, Privacy, Terms).
 *
 * These were linked from the footer and the signup consent checkbox but had
 * never been built, so every one of them rendered a blank screen.
 *
 * The commercial and legal specifics below (delivery lead times, the returns
 * window, the registered entity details) are drawn from what the application
 * actually implements — the R650 free-delivery threshold and R100 flat rate come
 * from ShopContext. Anything the codebase could not tell us is marked clearly
 * rather than invented.
 */
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import { SEO } from '@/components/SEO';
import { FREE_DELIVERY_THRESHOLD, STANDARD_SHIPPING } from '@/context/ShopContext';

const CONTACT_EMAIL = 'hello@marplecompany.co.za';

function PageShell({
  title,
  intro,
  path,
  description,
  children,
}: {
  title: string;
  intro?: string;
  path: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen" style={{ background: '#F7F5EE' }}>
      <SEO title={`${title} | Marple & Co`} description={description} path={path} />

      <section className="pt-32 pb-14" style={{ background: 'linear-gradient(135deg, #567D38 0%, #1a3d2a 100%)' }}>
        <div className="container-marple text-center max-w-3xl mx-auto">
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-white mb-4">{title}</h1>
          {intro && <p className="text-lg text-white/80">{intro}</p>}
        </div>
      </section>

      <div className="container-marple py-14">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl p-8 sm:p-10" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <div className="prose-marple space-y-5" style={{ color: '#3C4A38' }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="font-serif text-xl font-bold pt-4" style={{ color: '#1A2E12' }}>
      {children}
    </h2>
  );
}

/* ── Contact ──────────────────────────────────────────────────────────────── */

export function Contact() {
  return (
    <PageShell
      title="Contact Us"
      intro="We read every message — and we answer like humans, not a ticket queue."
      path="/contact"
      description="Get in touch with Marple & Co — email, phone and postal details, plus what to expect when you write to us."
    >
      <p>
        The fastest way to reach us is email. We aim to reply to every enquiry within one to two
        business days, Monday to Friday.
      </p>

      <div className="grid sm:grid-cols-2 gap-4 not-prose pt-2">
        <a
          href={`mailto:${CONTACT_EMAIL}`}
          className="flex items-start gap-3 p-4 rounded-xl border transition-colors hover:bg-[#F7F5EE]"
          style={{ borderColor: '#e8e2d9' }}
        >
          <Mail className="w-5 h-5 mt-0.5" style={{ color: '#567D38' }} />
          <span>
            <span className="block font-semibold" style={{ color: '#1A2E12' }}>Email</span>
            <span className="text-sm" style={{ color: '#6B7B6E' }}>{CONTACT_EMAIL}</span>
          </span>
        </a>

        <div className="flex items-start gap-3 p-4 rounded-xl border" style={{ borderColor: '#e8e2d9' }}>
          <Phone className="w-5 h-5 mt-0.5" style={{ color: '#567D38' }} />
          <span>
            <span className="block font-semibold" style={{ color: '#1A2E12' }}>Phone</span>
            <span className="text-sm" style={{ color: '#6B7B6E' }}>Weekdays, 09:00–17:00 SAST</span>
          </span>
        </div>

        <div className="flex items-start gap-3 p-4 rounded-xl border sm:col-span-2" style={{ borderColor: '#e8e2d9' }}>
          <MapPin className="w-5 h-5 mt-0.5" style={{ color: '#567D38' }} />
          <span>
            <span className="block font-semibold" style={{ color: '#1A2E12' }}>Based in</span>
            <span className="text-sm" style={{ color: '#6B7B6E' }}>Johannesburg, South Africa</span>
          </span>
        </div>
      </div>

      <H2>Order enquiries</H2>
      <p>
        Please include your order number and the email address you checked out with — it lets us
        pull up your order straight away instead of going back and forth.
      </p>

      <H2>Skin and product questions</H2>
      <p>
        If you're not sure what suits your skin, the{' '}
        <Link to="/skin-analysis" className="underline" style={{ color: '#567D38' }}>
          free skin analysis
        </Link>{' '}
        is the best place to start. It builds a regimen around what your skin actually shows us. For
        anything it doesn't answer, write to us.
      </p>
    </PageShell>
  );
}

/* ── FAQ ──────────────────────────────────────────────────────────────────── */

const FAQS: { q: string; a: React.ReactNode }[] = [
  {
    q: 'How much is delivery?',
    a: (
      <>
        Delivery is a flat R{STANDARD_SHIPPING.toFixed(2)} anywhere in South Africa, and free on
        orders over R{FREE_DELIVERY_THRESHOLD.toFixed(2)}. Your cart shows exactly how much more you
        need to add to qualify.
      </>
    ),
  },
  {
    q: 'Do you ship outside South Africa?',
    a: <>Not yet. We currently deliver within South Africa only.</>,
  },
  {
    q: 'Is the skin analysis really free?',
    a: (
      <>
        Yes. You answer a short set of questions, optionally upload photos, and get a regimen built
        around your skin — at no cost and with no obligation to buy anything.
      </>
    ),
  },
  {
    q: 'What happens to the photos I upload?',
    a: (
      <>
        They're used to generate your skin analysis and to track your progress over time in your
        member portal. See our{' '}
        <Link to="/privacy" className="underline" style={{ color: '#567D38' }}>
          Privacy Policy
        </Link>{' '}
        for how they're stored and how to have them deleted.
      </>
    ),
  },
  {
    q: 'Are your products suitable for sensitive skin?',
    a: (
      <>
        Our range is formulated with sensitive skin in mind and is dermatologically tested. That
        said, everyone's skin differs — patch test a new product on your inner forearm for 24 hours
        before applying it to your face.
      </>
    ),
  },
  {
    q: 'Do I need an account to order?',
    a: (
      <>
        No, you can check out as a guest. An account lets you keep your regimen, your assessment
        history and your progress photos in one place.
      </>
    ),
  },
  {
    q: 'How do I become an agent?',
    a: (
      <>
        Apply through the{' '}
        <Link to="/member/become-agent" className="underline" style={{ color: '#567D38' }}>
          agent application
        </Link>{' '}
        in your member portal. Applications are reviewed before approval.
      </>
    ),
  },
];

export function FAQ() {
  return (
    <PageShell
      title="Frequently Asked Questions"
      intro="Delivery, skin analysis, ingredients and accounts."
      path="/faq"
      description="Answers to common questions about Marple & Co delivery, the free skin analysis, our products and member accounts."
    >
      {FAQS.map(item => (
        <div key={item.q} className="pb-4 border-b last:border-0" style={{ borderColor: '#e8e2d9' }}>
          <h2 className="font-semibold mb-1.5" style={{ color: '#1A2E12' }}>{item.q}</h2>
          <p style={{ color: '#3C4A38' }}>{item.a}</p>
        </div>
      ))}

      <p className="pt-2">
        Still stuck?{' '}
        <Link to="/contact" className="underline" style={{ color: '#567D38' }}>
          Get in touch
        </Link>
        .
      </p>
    </PageShell>
  );
}

/* ── Shipping & Returns ───────────────────────────────────────────────────── */

export function ShippingReturns() {
  return (
    <PageShell
      title="Shipping & Returns"
      intro="What it costs, how long it takes, and what happens if something isn't right."
      path="/shipping-returns"
      description="Marple & Co delivery costs and lead times within South Africa, plus how to return or exchange an order."
    >
      <H2>Delivery cost</H2>
      <p>
        Standard delivery is a flat <strong>R{STANDARD_SHIPPING.toFixed(2)}</strong> anywhere in
        South Africa. Orders over <strong>R{FREE_DELIVERY_THRESHOLD.toFixed(2)}</strong> ship free —
        your cart shows how close you are to qualifying.
      </p>

      <H2>Where we deliver</H2>
      <p>We currently deliver within South Africa only.</p>

      <H2>Dispatch</H2>
      <p>
        Orders are packed and dispatched on business days. You'll receive a confirmation email when
        your order is placed, and tracking details once it's handed to the courier. Orders placed
        over a weekend or public holiday are processed the next business day.
      </p>

      <H2>If something arrives damaged</H2>
      <p>
        Tell us within 7 days of delivery at{' '}
        <a href={`mailto:${CONTACT_EMAIL}`} className="underline" style={{ color: '#567D38' }}>
          {CONTACT_EMAIL}
        </a>{' '}
        with your order number and a photo of the item and packaging. We'll arrange a replacement or
        a refund — you won't be out of pocket for a courier fault.
      </p>

      <H2>Returns and exchanges</H2>
      <p>
        Unopened products in their original packaging may be returned for a refund or exchange.
        Contact us with your order number before sending anything back so we can confirm the return
        address and reference.
      </p>
      <p>
        For hygiene and safety reasons, products that have been opened or used cannot be returned
        unless they are faulty or were incorrectly supplied.
      </p>
      <p>
        None of this limits your rights under the Consumer Protection Act 68 of 2008.
      </p>

      <H2>Reactions and sensitivities</H2>
      <p>
        If a product causes a reaction, stop using it and contact us. We would rather find you
        something that suits your skin than have you persevere with something that doesn't.
      </p>
    </PageShell>
  );
}

/* ── Privacy ──────────────────────────────────────────────────────────────── */

export function Privacy() {
  return (
    <PageShell
      title="Privacy Policy"
      intro="What we collect, why, and how to get it removed."
      path="/privacy"
      description="How Marple & Co collects, uses, stores and protects your personal information, including skin analysis photos, under POPIA."
    >
      <p>
        This policy explains how Marple &amp; Co handles your personal information, in line with the
        Protection of Personal Information Act 4 of 2013 (POPIA).
      </p>

      <H2>What we collect</H2>
      <ul className="list-disc pl-5 space-y-1.5">
        <li><strong>Account details</strong> — your name, surname, email address and password.</li>
        <li><strong>Order details</strong> — delivery address, phone number and order history.</li>
        <li>
          <strong>Skin assessment data</strong> — the answers you give in the skin analysis, and any
          photographs you choose to upload.
        </li>
        <li>
          <strong>Agent details</strong> — if you apply to become an agent, the information on your
          application, plus your sales and commission records.
        </li>
      </ul>

      <H2>Photographs</H2>
      <p>
        Uploading photographs is optional. Where you do upload them, they are used to generate your
        skin analysis and to let you track changes in your skin over time in your member portal. They
        are not used for advertising and are not sold. You can ask us to delete them at any time.
      </p>

      <H2>Why we process it</H2>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>To create and run your account.</li>
        <li>To process, deliver and support your orders.</li>
        <li>To produce your skin analysis and recommended regimen.</li>
        <li>To administer the agent programme, where it applies to you.</li>
        <li>To send marketing, only where you have opted in.</li>
      </ul>

      <H2>Marketing</H2>
      <p>
        Marketing consent is separate from creating an account, and you can withdraw it at any time
        from your account settings or via the unsubscribe link in any marketing email.
      </p>

      <H2>Sharing</H2>
      <p>
        We share personal information only with the service providers needed to run the business —
        payment processors, couriers and our hosting provider — and only to the extent they need it.
        We do not sell your personal information.
      </p>

      <H2>Storage and security</H2>
      <p>
        Your information is stored in our database with passwords held only as salted hashes, never
        in readable form. Access is restricted to those who need it to operate the service.
      </p>

      <H2>Your rights under POPIA</H2>
      <p>You may ask us to:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Confirm what personal information we hold about you.</li>
        <li>Correct anything that is inaccurate or out of date.</li>
        <li>Delete your account, your assessment history and your uploaded photographs.</li>
        <li>Stop sending you marketing.</li>
      </ul>
      <p>
        Email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`} className="underline" style={{ color: '#567D38' }}>
          {CONTACT_EMAIL}
        </a>{' '}
        and we'll action it. You also have the right to complain to the Information Regulator of
        South Africa.
      </p>

      <H2>Cookies</H2>
      <p>
        We use cookies and similar browser storage to keep you signed in and to remember your cart
        between visits. Blocking them in your browser will stop those features working, but you can
        still browse the site.
      </p>

      <H2>Changes</H2>
      <p>
        If we make a material change to this policy we'll say so on this page. Please check back from
        time to time.
      </p>
    </PageShell>
  );
}

/* ── Terms ────────────────────────────────────────────────────────────────── */

export function Terms() {
  return (
    <PageShell
      title="Terms of Service"
      intro="The agreement between you and Marple & Co."
      path="/terms"
      description="The terms governing use of the Marple & Co website, orders, member accounts and the agent programme."
    >
      <p>
        By using this website, creating an account or placing an order, you agree to these terms.
      </p>

      <H2>Accounts</H2>
      <p>
        You are responsible for keeping your login details secure and for activity under your
        account. Tell us promptly if you believe someone else has gained access to it. Please keep
        the information on your account accurate and current.
      </p>

      <H2>Orders and pricing</H2>
      <p>
        Prices are shown in South African Rand and include VAT where applicable. Placing an order is
        an offer to buy; a contract of sale comes into effect when we confirm and dispatch your
        order. Delivery charges are shown before you confirm payment.
      </p>
      <p>
        We take care to describe and price products accurately. Where an obvious error has occurred,
        we may decline or cancel the affected order and will refund you in full.
      </p>

      <H2>Availability</H2>
      <p>
        Our products are handmade in small batches, so stock can be limited. If we cannot fulfil part
        of your order we'll contact you and refund the affected items.
      </p>

      <H2>Skin analysis is not medical advice</H2>
      <p>
        The skin analysis and any regimen it produces are cosmetic guidance, not a diagnosis and not
        medical advice. They do not replace consulting a doctor or dermatologist. If you have a
        diagnosed skin condition, are pregnant or breastfeeding, or are using prescription
        treatments, speak to a healthcare professional before changing your routine. Patch test new
        products, and stop using anything that causes a reaction.
      </p>

      <H2>Agent programme</H2>
      <p>
        Agent applications are reviewed before approval, and approval is at our discretion.
        Commission is earned on qualifying sales in line with the programme rules given to you on
        approval. Agents must describe our products honestly and may not make medical claims about
        them. We may suspend or end an agent's participation for breach of these terms.
      </p>

      <H2>Acceptable use</H2>
      <p>
        Please don't attempt to disrupt the site, gain unauthorised access to any part of it, or
        misuse other users' information. We may suspend access where these terms are breached.
      </p>

      <H2>Our content</H2>
      <p>
        The text, images, branding and design on this site belong to Marple &amp; Co and may not be
        reproduced commercially without our permission.
      </p>

      <H2>Liability</H2>
      <p>
        Nothing in these terms excludes any liability that cannot lawfully be excluded, including
        under the Consumer Protection Act 68 of 2008.
      </p>

      <H2>Governing law</H2>
      <p>These terms are governed by the law of the Republic of South Africa.</p>

      <H2>Contact</H2>
      <p>
        Questions about these terms:{' '}
        <a href={`mailto:${CONTACT_EMAIL}`} className="underline" style={{ color: '#567D38' }}>
          {CONTACT_EMAIL}
        </a>
        .
      </p>
    </PageShell>
  );
}
